import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import http from "../config/axios";
import { getCrispSegment } from "../hooks/useCrispContext";

const CrispTicketFormModal = ({
  open,
  onClose,
  onTicketCreated,
  onViewTickets,
}) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [serial, setSerial] = useState("");
  const [address, setAddress] = useState("");
  const [category, setCategory] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const CATEGORIES = [
    "Activation",
    "Porting",
    "Billing",
    "Technical Issue",
    "General Inquiry",
  ];

  const resetForm = () => {
    setName("");
    setPhone("");
    setSerial("");
    setAddress("");
    setCategory("");
    setSubmitted(false);
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      if (window.$crisp) {
        window.$crisp.push([
          "set",
          "session:data",
          [
            [
              ["ticket_name", name.trim()],
              ["ticket_serial", serial.trim()],
              ["page_url", window.location.href],
            ].filter(([, value]) => value),
          ],
        ]);
      }

      const categoryLabel = category || "General Inquiry";
      const subject = `${categoryLabel} support request`;
      const messageLines = [
        `Category: ${categoryLabel}`,
        name.trim() ? `Name: ${name.trim()}` : null,
        phone.trim() ? `End-user Phone #: ${phone.trim()}` : null,
        serial.trim() ? `Serial Number: ${serial.trim()}` : null,
        address.trim() ? `Physical Address: ${address.trim()}` : null,
        `Page: ${window.location.href}`,
      ].filter(Boolean);
      const body = messageLines.join("\n");

      const { data } = await http.post("crisp/tickets", {
        name: name.trim() || undefined,
        phone: phone.trim() || undefined,
        serial: serial.trim() || undefined,
        address: address.trim() || undefined,
        category: category || undefined,
        subject,
        body,
        domain: window.location.hostname,
        segment: getCrispSegment(),
        source: "bulk-automation-ui",
      });

      const ticketId = data?.ticket_id || data?.session_id;
      if (ticketId) {
        setSubmitted(true);
        setTimeout(() => {
          handleClose();
          onTicketCreated?.(ticketId);
        }, 900);
        return;
      }

      setError("Ticket was created but no ticket id was returned.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === "string" ? detail : "Failed to create support ticket."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle>New support ticket</DialogTitle>
        <DialogContent>
          {submitted ? (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <CheckCircleOutlineIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="body1">
                Your ticket has been created. Our support team will get back to you
                shortly.
              </Typography>
            </Box>
          ) : (
            <>
              <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                sx={{ mt: 1.5, mb: 1.5 }}
              />
              <TextField
                label="End-user Phone #"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                sx={{ mb: 1.5 }}
                type="tel"
              />
              <TextField
                label="Serial Number"
                value={serial}
                onChange={(e) => setSerial(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                sx={{ mb: 1.5 }}
              />
              <TextField
                label="Physical Address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                sx={{ mb: 1.5 }}
                multiline
                rows={2}
              />
              <FormControl variant="outlined" size="small" fullWidth sx={{ mb: 1.5 }}>
                <InputLabel>Ticket Category</InputLabel>
                <Select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  label="Ticket Category"
                >
                  {CATEGORIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {error && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                  {error}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                All fields are optional but help us resolve your issue faster.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              handleClose();
              onViewTickets?.();
            }}
            disabled={submitted}
          >
            View your tickets
          </Button>
          <Button onClick={handleClose} disabled={submitted}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || submitted}
          >
            {submitting ? "Submitting…" : "Create ticket"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default CrispTicketFormModal;
